/** @format */

import {
  Box,
  IconButton,
  Chip,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ApplicationYear, {
  TypeOfRequest,
} from '../../../common/dropdowns/ApplicationYear';
import { AllApplicantDto } from '../../applicant/Models';
import SearchBar from './SearchBar';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAppDispatch } from '../../../app/hooks';
import { fetchAllApplicantThunk, ExportToExcelThunk } from '../thunks';
import {
  APPLICATION_STAGE,
  normalizeApplicationStage,
} from '../../../common/models/enums/applicationStage';
import AdvancedExportApplicationsModal from './AdvancedExportApplicationsModal';

type OutcomeChip =
  | 'assessed'
  | 'completed'
  | 'incomplete'
  | 'returning'
  | null;

type StageDef = {
  stage: APPLICATION_STAGE;
  label: string;
  predicate: (a: AllApplicantDto) => boolean;
};

const STAGE_FILTER_DEFINITIONS: StageDef[] = [
  {
    stage: APPLICATION_STAGE.All,
    label: 'All',
    predicate: () => true,
  },
  {
    stage: APPLICATION_STAGE.NotAssessed,
    label: 'Not Assessed',
    predicate: (a) => !a.isAssessed,
  },
  {
    stage: APPLICATION_STAGE.Assessed,
    label: 'Assessed',
    predicate: (a) => !!a.isAssessed,
  },
  {
    stage: APPLICATION_STAGE.ApplicationSubmitted,
    label: 'Application Submitted',
    predicate: (a) =>
      normalizeApplicationStage(a.applicationStage) ===
      APPLICATION_STAGE.ApplicationSubmitted,
  },
  {
    stage: APPLICATION_STAGE.StudyDetails,
    label: 'Study Details',
    predicate: (a) =>
      normalizeApplicationStage(a.applicationStage) ===
      APPLICATION_STAGE.StudyDetails,
  },
  {
    stage: APPLICATION_STAGE.InstitutionDetails,
    label: 'Institution Details',
    predicate: (a) =>
      normalizeApplicationStage(a.applicationStage) ===
      APPLICATION_STAGE.InstitutionDetails,
  },
  {
    stage: APPLICATION_STAGE.DocumentsUpload,
    label: 'Documents Upload',
    predicate: (a) =>
      normalizeApplicationStage(a.applicationStage) ===
      APPLICATION_STAGE.DocumentsUpload,
  },
  {
    stage: APPLICATION_STAGE.LegalGuardianDetails,
    label: 'Legal Guardian Details',
    predicate: (a) =>
      normalizeApplicationStage(a.applicationStage) ===
      APPLICATION_STAGE.LegalGuardianDetails,
  },
  {
    stage: APPLICATION_STAGE.AddressDetails,
    label: 'Address Details',
    predicate: (a) =>
      normalizeApplicationStage(a.applicationStage) ===
      APPLICATION_STAGE.AddressDetails,
  },
];

/** `null` = All (no year filter). 1–5 match API year-of-study ids. */
const YEAR_OF_STUDY_CHIPS: { id: number | null; label: string }[] = [
  { id: null, label: 'All' },
  { id: 1, label: '1st Year' },
  { id: 2, label: '2nd Year' },
  { id: 3, label: '3rd Year' },
  { id: 4, label: '4th Year' },
  { id: 5, label: 'CTA/PGDA' },
];

function applicantMatchesYearOfStudyId(
  a: AllApplicantDto,
  yearId: number
): boolean {
  const raw = a.effectiveYearOfStudyId;
  if (raw === undefined || raw === null) return false;
  const id = Number(raw);
  if (!Number.isFinite(id) || id < 1) return false;
  return id === yearId;
}

function filterApplicantsBySearch(
  rows: AllApplicantDto[],
  q: string
): AllApplicantDto[] {
  return rows.filter(
    (s) =>
      s.personalDetails.name.toLowerCase().includes(q) ||
      s.applicationStage.toString().toLowerCase().includes(q) ||
      s.assessmentStatus.toLowerCase().includes(q) ||
      s.personalDetails.surname.toLowerCase().includes(q) ||
      s.personalDetails.identityNumber.toLowerCase().includes(q) ||
      s.personalDetails.email.toLowerCase().includes(q)
  );
}

interface Props {
  filter: APPLICATION_STAGE;
  applicants: AllApplicantDto[];
  setFilterdData: React.Dispatch<React.SetStateAction<AllApplicantDto[]>>;
  setFilterd: React.Dispatch<React.SetStateAction<APPLICATION_STAGE>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setIsSearchEmpty: React.Dispatch<React.SetStateAction<boolean>>;
  search: string;
  setPage: React.Dispatch<React.SetStateAction<number>>;
}

export default function FilterApplications({
  filter,
  applicants,
  setFilterdData,
  setFilterd,
  setSearch,
  search,
  setIsSearchEmpty,
  setPage,
}: Props) {
  const [searchParams] = useSearchParams();
  const { year: yearRouteParam } = useParams<{ year: string }>();
  const navigate = useNavigate();
  const yearFromQuery = searchParams.get('year');
  const returningFromUrl =
    searchParams.get('returning') === '1' ||
    searchParams.get('returning') === 'true';

  const [selectedYear, setSelectedYear] = useState<number>(() => {
    if (yearFromQuery) {
      const y = parseInt(yearFromQuery, 10);
      if (Number.isFinite(y)) return y;
    }
    if (yearRouteParam) {
      const y = parseInt(yearRouteParam, 10);
      if (Number.isFinite(y)) return y;
    }
    return new Date().getFullYear();
  });
  const returningRouteAppliedRef = useRef(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState(false);
  const [advancedExportOpen, setAdvancedExportOpen] = useState(false);
  const [activeOutcomeChip, setActiveOutcomeChip] =
    useState<OutcomeChip>(null);
  /** `null` = All years (no filter). */
  const [activeYearOfStudyFilter, setActiveYearOfStudyFilter] = useState<
    number | null
  >(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    let y: number | undefined;
    if (yearFromQuery) {
      const parsed = parseInt(yearFromQuery, 10);
      if (Number.isFinite(parsed)) y = parsed;
    } else if (yearRouteParam) {
      const parsed = parseInt(yearRouteParam, 10);
      if (Number.isFinite(parsed)) y = parsed;
    }
    if (y !== undefined) {
      const nextYear = y;
      setSelectedYear((prev) => (prev === nextYear ? prev : nextYear));
    }
  }, [yearFromQuery, yearRouteParam]);

  const setYear = useCallback(
    (y: number) => {
      if (!Number.isFinite(y)) return;
      setSelectedYear(y);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('year');
      const qs = nextParams.toString();
      navigate(
        { pathname: `/applications/${y}`, search: qs ? `?${qs}` : '' },
        { replace: true }
      );
    },
    [navigate, searchParams]
  );

  const handleExport = async () => {
    try {
      setIsExporting(true);

      const result = await dispatch(
        ExportToExcelThunk({
          stage: filter,
          year: selectedYear,
        })
      );

      if (ExportToExcelThunk.rejected.match(result)) {
        console.error('Export failed:', result.error);
      }
    } catch (error) {
      console.error('Unexpected error during export:', error);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await dispatch(fetchAllApplicantThunk(selectedYear));
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [selectedYear, dispatch]);

  useEffect(() => {
    returningRouteAppliedRef.current = false;
  }, [selectedYear]);

  useEffect(() => {
    setActiveOutcomeChip(null);
    setActiveYearOfStudyFilter(null);
  }, [selectedYear, applicants]);

  useEffect(() => {
    if (!returningFromUrl) {
      returningRouteAppliedRef.current = false;
    }
  }, [returningFromUrl]);

  useEffect(() => {
    if (
      !returningFromUrl ||
      returningRouteAppliedRef.current ||
      applicants.length === 0
    ) {
      return;
    }
    setActiveOutcomeChip('returning');
    setActiveYearOfStudyFilter(null);
    setFilterd(APPLICATION_STAGE.All);
    setSearch('');
    setPage(0);
    setIsSearchEmpty(true);
    returningRouteAppliedRef.current = true;
  }, [
    returningFromUrl,
    applicants,
    setFilterd,
    setSearch,
    setPage,
    setIsSearchEmpty,
  ]);

  const baseAfterStageOrOutcome = useMemo(() => {
    if (activeOutcomeChip === 'assessed') {
      return applicants.filter((a) => a.isAssessed);
    }
    if (activeOutcomeChip === 'completed') {
      return applicants.filter((a) => !!a.dateCompleted);
    }
    if (activeOutcomeChip === 'incomplete') {
      return applicants.filter((a) => !a.dateCompleted);
    }
    if (activeOutcomeChip === 'returning') {
      return applicants.filter((a) => a.isReturningApplicant);
    }
    if (filter === APPLICATION_STAGE.All) {
      return applicants;
    }
    const def = STAGE_FILTER_DEFINITIONS.find((d) => d.stage === filter);
    const pred = def?.predicate ?? (() => false);
    return applicants.filter(pred);
  }, [applicants, filter, activeOutcomeChip]);

  const displayBase = useMemo(() => {
    if (activeYearOfStudyFilter === null) {
      return baseAfterStageOrOutcome;
    }
    return baseAfterStageOrOutcome.filter((a) =>
      applicantMatchesYearOfStudyId(a, activeYearOfStudyFilter)
    );
  }, [baseAfterStageOrOutcome, activeYearOfStudyFilter]);

  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (q.length >= 3) {
      setIsSearchEmpty(false);
      setFilterdData(filterApplicantsBySearch(displayBase, q));
    } else {
      setFilterdData(displayBase);
      setIsSearchEmpty(q.length === 0);
    }
  }, [displayBase, search, setFilterdData, setIsSearchEmpty]);

  const applyStageFilter = (stage: APPLICATION_STAGE) => {
    setActiveOutcomeChip(null);
    setFilterd(stage);
    setSearch('');
    setPage(0);
  };

  const applyOutcomeFilter = (outcome: Exclude<OutcomeChip, null>) => {
    setActiveOutcomeChip(outcome);
    setFilterd(
      outcome === 'assessed'
        ? APPLICATION_STAGE.Assessed
        : APPLICATION_STAGE.All
    );
    setSearch('');
    setPage(0);
  };

  const applyYearOfStudyChip = (yearId: number | null) => {
    setActiveYearOfStudyFilter(yearId);
    setSearch('');
    setPage(0);
  };

  const assessedCount = applicants.filter(
    (a: AllApplicantDto) => a.isAssessed
  ).length;

  const completedCount = applicants.filter(
    (a: AllApplicantDto) => !!a.dateCompleted
  ).length;
  const incompleteCount = applicants.length - completedCount;

  const returningCount = applicants.filter((a) => a.isReturningApplicant)
    .length;

  const hasApplications = applicants.length > 0;

  return (
    <Box sx={{ mb: 3, background: '#fff', borderRadius: 2, boxShadow: 1, p: 2 }}>
      {!isLoading && !hasApplications && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            p: 2,
            backgroundColor: '#f8f9fa',
            borderRadius: 1,
            borderLeft: '3px solid #1976d2',
          }}>
          <InfoOutlinedIcon color="action" fontSize="small" sx={{ mr: 1 }} />
          <Typography variant="body2" color="text.secondary">
            No applications for {selectedYear}
          </Typography>
        </Box>
      )}

      {/* Toolbar: refresh + year + search (flush); export right */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          width: '100%',
        }}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
            flex: 1,
            minWidth: 0,
          }}>
          <IconButton
            onClick={() => dispatch(fetchAllApplicantThunk(selectedYear))}
            aria-label="Refresh applications"
            sx={{
              flexShrink: 0,
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.04)',
              },
            }}>
            <RefreshIcon />
          </IconButton>
          <Box sx={{ minWidth: 120, flexShrink: 0 }}>
            <ApplicationYear
              request={TypeOfRequest.AllApplications}
              onYearChange={setYear}
              selectedYear={selectedYear}
              disabled={false}
            />
          </Box>
          <Box
            sx={{
              flex: '1 1 66.667%',
              minWidth: 200,
              maxWidth: { xs: '100%', sm: '66.667%' },
            }}>
            <SearchBar search={search} setSearch={setSearch} />
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexShrink: 0,
            gap: 1,
            ml: { sm: 'auto' },
            flexWrap: 'wrap',
            alignItems: 'center',
          }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setAdvancedExportOpen(true)}
            disabled={!hasApplications}>
            Advanced export
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExport}
            disabled={isExporting || !hasApplications}
            startIcon={
              isExporting ? (
                <CircularProgress size={20} />
              ) : (
                <FileDownloadIcon />
              )
            }>
            {isExporting ? 'Exporting...' : 'Export'}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          mb: 1.5,
          pl: 0,
        }}>
        {STAGE_FILTER_DEFINITIONS.map(({ stage, label, predicate }) => {
          const count = applicants.filter(predicate).length;
          const selected =
            filter === stage && activeOutcomeChip === null;
          return (
            <Chip
              key={stage}
              label={`${label}: ${count}`}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              clickable
              onClick={() => applyStageFilter(stage)}
            />
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          mb: 1.5,
          pl: 0,
        }}>
        {YEAR_OF_STUDY_CHIPS.map(({ id, label }) => {
          const count =
            id === null
              ? applicants.length
              : applicants.filter((a) => applicantMatchesYearOfStudyId(a, id))
                  .length;
          const selected = activeYearOfStudyFilter === id;
          return (
            <Chip
              key={id === null ? 'all-years' : id}
              label={`${label}: ${count}`}
              color={selected ? 'secondary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              clickable
              onClick={() => applyYearOfStudyChip(id)}
            />
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          alignItems: 'center',
          mb: 2,
          pl: 0,
        }}>
        <Chip
          label={`Assessed: ${assessedCount}`}
          color="success"
          variant={
            activeOutcomeChip === 'assessed' ? 'filled' : 'outlined'
          }
          clickable
          onClick={() => applyOutcomeFilter('assessed')}
        />
        <Chip
          label={`Completed: ${completedCount}`}
          color="info"
          variant={
            activeOutcomeChip === 'completed' ? 'filled' : 'outlined'
          }
          clickable
          onClick={() => applyOutcomeFilter('completed')}
        />
        <Chip
          label={`Incomplete: ${incompleteCount}`}
          color="warning"
          variant={
            activeOutcomeChip === 'incomplete' ? 'filled' : 'outlined'
          }
          clickable
          onClick={() => applyOutcomeFilter('incomplete')}
        />
        <Chip
          label={`Returning: ${returningCount}`}
          color="info"
          variant={
            activeOutcomeChip === 'returning' ? 'filled' : 'outlined'
          }
          clickable
          onClick={() => applyOutcomeFilter('returning')}
        />
      </Box>

      <AdvancedExportApplicationsModal
        open={advancedExportOpen}
        onClose={() => setAdvancedExportOpen(false)}
        year={selectedYear}
        hasApplications={hasApplications}
      />
    </Box>
  );
}
